// Sensei AI — owner-only client helper.
//
// The Sensei sits behind a SENSEI_OWNER_CODES check on the backend. The
// frontend reads the active access code from AccessGate's localStorage
// session and forwards it as ``X-Access-Code`` on every Sensei request.
// We expose a tiny eligibility cache so the UI doesn't ping the server
// on every render.

import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "bc_access_session";
const ELIGIBLE_CACHE_KEY = "bc_sensei_eligible";

function getAccessCode() {
  try {
    const session = JSON.parse(localStorage.getItem(SESSION_KEY) || "null");
    return session?.code || null;
  } catch {
    return null;
  }
}

/**
 * Returns ``true`` only if the active access code is registered as an owner
 * on the backend. Result is cached for 10 minutes AND tied to the active
 * access code — if the user logs in with a different BCRK-… the cache is
 * automatically invalidated.
 */
export async function checkSenseiEligibility(force = false) {
  const code = getAccessCode();
  if (!code) return false;
  if (!force) {
    try {
      const cached = JSON.parse(localStorage.getItem(ELIGIBLE_CACHE_KEY) || "null");
      const fresh = cached
        && cached.code === code
        && (Date.now() - cached.t) < 10 * 60 * 1000;   // 10 minutes
      if (fresh) return Boolean(cached.owner);
    } catch { /* ignore */ }
  }
  try {
    const r = await axios.get(`${API}/sensei/eligible`, {
      headers: { "X-Access-Code": code },
      timeout: 5000,
    });
    const owner = Boolean(r.data?.owner);
    localStorage.setItem(ELIGIBLE_CACHE_KEY, JSON.stringify({ owner, code, t: Date.now() }));
    return owner;
  } catch {
    return false;
  }
}

/**
 * Trigger the heavy LLM analysis. ``payload`` may contain freshly-loaded
 * match data so the backend doesn't have to pull anything else.
 */
export async function runSenseiAnalysis(fixtureId, payload = {}) {
  const code = getAccessCode();
  if (!code) {
    throw new Error("Nessun codice di accesso attivo");
  }
  const r = await axios.post(`${API}/sensei/analyze/${fixtureId}`, payload, {
    headers: { "X-Access-Code": code },
    timeout: 60000,
  });
  return r.data;
}
